import React, { useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TableColumn from './TableColumn';
import noData from '../../../../assets/img/svg/table/no-data-available.svg';
import dataFailed from '../../../../assets/img/svg/table/load-data-failed.svg';
import { t } from 'i18next';
import { useColumns } from '../../../../hook/useColumns';
import { store } from '../../../../store/store';
import { CHUNK_SCROLL_VISIBLE_SIZE, CHUNK_SIZE } from '../../../Page/StatisticPage';

interface TableProps {
  data: any[];
  dataLength: number;
  showFooter: boolean;
  error?: string | null;
  className?: string;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void; // optional raw onScroll
  onNearBottom?: () => void; // optional callback when near bottom
  onNearTop?: () => void; // optional callback when near top
  maxHeight?: number | string; // scroll container height
  getCurrentChunkPosition: () => number;
}

const SCROLL_DELAY_MS = 700;

const Table: React.FC<TableProps> = ({
  data,
  showFooter,
  className,
  onScroll,
  onNearBottom,
  onNearTop,
  maxHeight = '60vh',
  getCurrentChunkPosition,
  dataLength,
  error,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Hooks for pinned/unpinned + order
  const { lockedColumns, pinnedColumns, unpinnedColumns } = useColumns();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    onScroll?.(e);
    const el = e.currentTarget;
    const isOnBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;
    const currentIndexPosition = getCurrentChunkPosition() * CHUNK_SIZE;

    if (isOnBottom && currentIndexPosition < dataLength) {
      onNearBottom?.();
      setTimeout(() => {
        el.scrollTop = el.scrollHeight / 2 + 100;
      }, SCROLL_DELAY_MS);
    }

    const isOnTop = el.scrollTop === 0;

    if (isOnTop && currentIndexPosition > CHUNK_SIZE * CHUNK_SCROLL_VISIBLE_SIZE) {
      onNearTop?.();
      setTimeout(() => {
        el.scrollTop = el.scrollHeight / 2 - 100;
      }, SCROLL_DELAY_MS);
    }
  };

  const getNoDataMessage = (): string => {
    if (error) {
      return t('general.dataLoadFailed');
    }

    return t('statisticPage.table.noData');
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let timeoutId: number | null = null;

    const handleScroll = () => {
      // Clear existing debounce
      if (timeoutId) clearTimeout(timeoutId);

      // Set up new debounce
      timeoutId = window.setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;

        const dynamicStop = store.getState().stats.shouldViewPerCurrency
          ? store.getState().stats.locationColumnHeight
          : 0;

        const maxScroll = scrollHeight - clientHeight - dynamicStop;

        // If scrolled past the "stop zone", bring it back smoothly
        if (scrollTop > maxScroll) {
          container.scrollTo({
            top: maxScroll,
            behavior: 'smooth',
          });
        }
      }, 350);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`table_container ${className}`}>
        <div
          className="table_wrapper"
          ref={tableContainerRef}
          onScroll={handleScroll}
          style={{ overflowY: 'auto', maxHeight, scrollBehavior: 'auto' }}
        >
          <div className="table">
            {lockedColumns.length === 0 && (
              <div className="no_data">
                <img className="no_data_img" src={error ? dataFailed : noData} alt={getNoDataMessage()} />
                <p className="no_data_message">{getNoDataMessage()}</p>
              </div>
            )}

            {/* Locked columns (Geo / CCY) */}
            {lockedColumns.map((col) => (
              <TableColumn
                key={`locked_${col.name}`}
                column={col}
                data={data}
                isFirstColumnAfterCCY={false}
                isLastColumn={false}
                showFooter={showFooter}
                className={col.name === t('cashRegister.table.header.actions') ? 'table_image' : className}
              />
            ))}

            {/* Pinned columns */}
            {pinnedColumns
              .filter((col) => col.isVisible)
              .map((col, i) => (
                <TableColumn
                  key={`pinned_${col.name}`}
                  column={col}
                  data={data}
                  isFirstColumnAfterCCY={i === 0}
                  isLastColumn={i + 1 === pinnedColumns.length}
                  showFooter={showFooter}
                  className={col.name === t('cashRegister.table.header.actions') ? 'table_image' : className}
                />
              ))}

            {/* Unpinned columns */}
            {unpinnedColumns
              .filter((col) => col.isVisible)
              .map((col, i) => (
                <TableColumn
                  key={`unpinned_${col.name}`}
                  column={col}
                  data={data}
                  isFirstColumnAfterCCY={i === 0}
                  isLastColumn={i + 1 === unpinnedColumns.length}
                  showFooter={showFooter}
                  className={col.name === t('cashRegister.table.header.actions') ? 'table_image' : className}
                />
              ))}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default Table;
